import { Avatar, Badge, Button, Card, Chip, EmptyState, Icon, Popover, PopoverItem, Skeleton, formatDate, formatMoney, formatTime, type ChipKind } from "@helmr/ui";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { doneFollowup, snoozeFollowup, type FollowupItem } from "../../api/followups";
import { NoAccess } from "../../app/NoAccess";
import { areaAllows } from "../../app/nav";
import { useServices } from "../../app/services";
import { addDays, today as localDate } from "../../lib/dates";
import { isUnmasked, telHref, whatsappHref } from "../../lib/phone";
import { FailureBody } from "../home/states";
import { useQueryClient } from "@tanstack/react-query";
import { StaffName } from "../directory/StaffName";
import { useStaffNames } from "../leads/hooks";
import { WorkNav } from "./Nav";
import { useDateParam, useFollowupList, useScope, type Scope } from "./hooks";
import { fu } from "./strings";
import { UndoBar, useUndoQueue } from "./undo";
import s from "./followups.module.css";

const t = fu.followups;
const chipKind = (kind: string): ChipKind => (kind === "renewal" ? "renewal" : kind === "due" ? "dues" : kind === "inactive" ? "inactive" : kind === "lead_trial" || kind === "lead_post_trial" ? "trial" : "enquiry");
const KINDS = ["renewal", "due", "inactive", "lead_trial", "lead_post_trial", "lead_first_response"] as const;

/** Relative to today, whichever date is being viewed. */
function dueLabel(f: FollowupItem, sc: Scope): string {
  const { locale, timeZone } = sc.ctx;
  const day = f.dueDate ?? (f.dueAt !== null ? localDate(f.dueAt, timeZone) : null);
  if (!day) return "";
  if (day === sc.date && f.dueAt !== null) return formatTime(f.dueAt, timeZone);
  return day === addDays(sc.date, -1) ? t.yesterday : formatDate(day, locale, "weekday");
}

function Skel() {
  return <div aria-busy="true" data-testid="followups-skeleton">{Array.from({ length: 6 }, (_, i) => <div className={s.skRow} key={i}><Skeleton width={30} height={30} /><div style={{ display: "grid", gap: 6 }}><Skeleton width="40%" height={13} /><Skeleton width="65%" height={12} /></div></div>)}</div>;
}

export function FollowupsPage() {
  const sc = useScope();
  const { client } = useServices();
  const qc = useQueryClient();
  const staff = useStaffNames();
  const { date, setDate, params, setParams } = useDateParam(sc);
  const kind = params.get("kind") ?? "";
  const ownerParam = params.get("owner") ?? "";
  const meId = sc.ctx.me?.staff.id;
  const ownerStaffId = ownerParam === "me" ? meId : ownerParam || undefined;
  const q = useFollowupList(sc, date, ownerStaffId);
  const queue = useUndoQueue();
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const setParam = (k: string, v: string) => { const next = new URLSearchParams(params); if (v) next.set(k, v); else next.delete(k); setParams(next, { replace: true }); };
  const hide = (key: string, on: boolean) => setHidden((x) => { const n = new Set(x); if (on) n.add(key); else n.delete(key); return n; });

  function act(f: FollowupItem, how: { type: "done" } | { type: "snooze"; until: string }) {
    const name = f.subject.name ?? "Follow-up";
    hide(f.key, true);
    const label = how.type === "done" ? t.undoDone(name) : t.undoSnooze(name, formatDate(how.until, sc.ctx.locale, "weekday"));
    queue.schedule(
      f.key,
      label,
      () => {
        void (how.type === "done" ? doneFollowup(client, f.key) : snoozeFollowup(client, f.key, how.until))
          .then(() => qc.invalidateQueries({ queryKey: ["followups"] }))
          // The server decides from here on: a snoozed row is due again on its date, so it must not stay hidden.
          .then(() => hide(f.key, false))
          .catch(() => { hide(f.key, false); showFail(name); });
      },
      () => hide(f.key, false),
    );
  }
  const [failure, setFailure] = useState<string | null>(null);
  const showFail = (name: string) => setFailure(t.failed(name));

  const items = useMemo(() => (q.data?.items ?? []).filter((f) => !hidden.has(f.key) && (!kind || f.kind === kind)), [q.data, hidden, kind]);
  const overdue = items.filter((f) => f.overdue);
  const due = items.filter((f) => !f.overdue && (f.dueDate === null || f.dueDate <= date));
  const later = items.filter((f) => !f.overdue && f.dueDate !== null && f.dueDate > date);
  const omitted = q.data?.omitted ?? [];

  if (sc.ctx.status !== "loading" && !areaAllows("followups", sc.ctx.has)) return <NoAccess />;

  return (
    <div className={s.page}>
      <title>Follow-ups · Helmr</title>
      <div className={s.head}>
        <div><h1 tabIndex={-1}>{t.title}</h1><p>{q.data ? t.sub(items.length, q.data.truncated) : " "}</p></div>
        <WorkNav />
      </div>

      <div className={s.filters} role="group" aria-label="Follow-up filters">
        <label className={s.inline}><span>{t.date}</span><input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label={t.date} /></label>
        {date !== sc.date ? <Button size="sm" onClick={() => setDate(sc.date)}>{t.today}</Button> : null}
        <select value={ownerParam} onChange={(e) => setParam("owner", e.target.value)} aria-label={t.owner}>
          <option value="">{t.owner}: {t.ownerAnyone}</option>
          <option value="me">{t.ownerMe}</option>
          {staff.list.filter((p) => p.id !== meId).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={kind} onChange={(e) => setParam("kind", e.target.value)} aria-label={t.kind}>
          <option value="">{t.allKinds}</option>
          {KINDS.map((k) => <option key={k} value={k}>{t.kindFilter[k]}</option>)}
        </select>
      </div>

      {failure ? <div className={s.alert} role="alert"><span>{failure}</span><button type="button" onClick={() => setFailure(null)}>Dismiss</button></div> : null}
      {omitted.map((f) => <p className={s.note} key={f} role="note">{f === "leads" ? t.omittedLeads : f === "members" ? t.omittedMembers : t.omitted(f)}</p>)}

      <Card>
        {q.isPending ? <Skel /> : q.error ? <FailureBody error={q.error} title={t.error} what="Follow-ups" onRetry={() => void q.refetch()} /> : items.length === 0 ? (
          <EmptyState icon="check" title={kind || ownerParam ? t.noMatch : t.empty} description={kind || ownerParam ? t.noMatchBody : t.emptyBody} />
        ) : (
          <>
            <Section title={t.overdue} tone="crit" rows={overdue} sc={sc} onAct={act} />
            <Section title={t.dueSection} rows={due} sc={sc} onAct={act} />
            <Section title={t.later} rows={later} sc={sc} onAct={act} />
            {q.data?.truncated ? <p className={s.note}>{t.truncated}</p> : null}
          </>
        )}
      </Card>
      <UndoBar items={queue.items} onUndo={queue.undo} />
    </div>
  );
}

type Act = (f: FollowupItem, how: { type: "done" } | { type: "snooze"; until: string }) => void;

function Section({ title, tone, rows, sc, onAct }: { title: string; tone?: "crit"; rows: FollowupItem[]; sc: Scope; onAct: Act }) {
  if (rows.length === 0) return null;
  return (
    <section aria-label={title}>
      <h2 className={s.sectionHead}>{title} <Badge tone={tone ?? "neutral"}>{rows.length}</Badge></h2>
      {rows.map((f) => <Row key={f.key} f={f} sc={sc} onAct={onAct} />)}
    </section>
  );
}

function detail(f: FollowupItem, sc: Scope): string {
  const bits: string[] = [t.kinds[f.kind] ?? "Follow-up"];
  if (f.kind === "due" && f.amountDue !== undefined && sc.ctx.me) bits.push(t.amount(formatMoney(f.amountDue, sc.ctx.me.business.currency, sc.ctx.locale)));
  if (f.kind === "renewal" && f.dueDate) bits.push(t.planEnds(formatDate(f.dueDate, sc.ctx.locale, "weekday")));
  return bits.join(" · ");
}

function Row({ f, sc, onAct }: { f: FollowupItem; sc: Scope; onAct: Act }) {
  const name = f.subject.name ?? "Unnamed";
  const callable = isUnmasked(f.subject.phone);
  const [picking, setPicking] = useState(false);
  const [until, setUntil] = useState("");
  const [bad, setBad] = useState(false);
  const min = addDays(sc.date, 1);
  const max = addDays(sc.date, 30);
  const href = f.subject.type === "lead" ? `/leads/${f.subject.id}` : `/members/${f.subject.id}`;
  // Done and Snooze need edit on the record's own feature (the API refuses otherwise).
  const canAct = sc.ctx.has(f.subject.type === "lead" ? "leads.edit" : "members.edit_profile");
  return (
    <div className={s.fi} data-testid="followup-row">
      <Avatar id={f.subject.id} name={name} />
      <div className={s.fiBody}>
        <div className={s.l1}>
          <Link to={href} className={s.nameLink}><b title={name}>{name}</b></Link>
          <Chip kind={chipKind(f.kind)} />
          <span className={`${s.due} ${f.overdue ? s.od : ""}`}>{dueLabel(f, sc)}</span>
        </div>
        <small className={s.detail}>{detail(f, sc)}</small>
        <div className={s.l3}>
          <span className={s.own}><StaffName id={f.ownerStaffId} name={f.ownerName} /></span>
          <div className={s.acts}>
            {canAct ? <button className={s.dn} aria-label={t.doneLabel(name)} onClick={() => onAct(f, { type: "done" })}><Icon name="check" />{t.done}</button> : null}
            {callable ? <a href={telHref(f.subject.phone!)} aria-label={t.callLabel(name)} title={t.call}><Icon name="phone" /></a> : null}
            {callable ? <a href={whatsappHref(f.subject.phone!)} target="_blank" rel="noopener noreferrer" aria-label={t.whatsappLabel(name)} title={t.whatsapp}><Icon name="chat" /></a> : null}
            {canAct ? <Popover role="menu" label={t.snoozeMenu} trigger={(tp) => <button {...tp} aria-label={t.snoozeLabel(name)} title={t.snooze}><Icon name="clock" /></button>}>
              {({ close }) => (
                <>
                  <PopoverItem onClick={() => { close(); onAct(f, { type: "snooze", until: addDays(sc.date, 1) }); }}>{t.tomorrow}</PopoverItem>
                  <PopoverItem onClick={() => { close(); onAct(f, { type: "snooze", until: addDays(sc.date, 3) }); }}>{t.threeDays}</PopoverItem>
                  <PopoverItem onClick={() => { close(); setPicking(true); }}>{t.pick}</PopoverItem>
                </>
              )}
            </Popover> : null}
          </div>
        </div>
        {picking ? (
          <form className={s.pickRow} noValidate onSubmit={(e) => { e.preventDefault(); if (until < min || until > max) { setBad(true); return; } setPicking(false); onAct(f, { type: "snooze", until }); }}>
            <input type="date" value={until} min={min} max={max} onChange={(e) => { setUntil(e.target.value); setBad(false); }} aria-label={t.pickLabel} required />
            <Button size="sm" type="submit" variant="primary">{t.pickGo}</Button>
            <Button size="sm" onClick={() => setPicking(false)}>Cancel</Button>
            <small className={bad ? s.errText : undefined}>{bad ? t.pickRange : t.pickHint}</small>
          </form>
        ) : null}
      </div>
    </div>
  );
}
